import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { URL_PARAMS } from '../../common/AppConstants';
import { useURLParams } from '../../common/AppUtils';
import { useRef, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography, Stepper, Step, StepLabel, Button, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { Card,
  CardHeader,
  CardContent,
  IconButton,
  Divider,
  Collapse,
  MenuItem,
  Stack,
}  from '@mui/material';
import { CircularProgress, Fade } from '@mui/material';
import Title from '../../components/Title';
import TimeTable from '../../components/TimeTable';
import UpgradeModal from '../../components/UpgradeModal';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, DEFAULT_ERROR_MESSAGE } from '../../common/AppConstants';
import { MANAGE_SUBSCRIPTION_URL } from '../../common/AppUrls';
import { titleCase, getRelatedDataMap } from '../../common/AppUtils'
import * as AppUrls from '../../common/AppUrls';

const TITLE = "Manage TimeTable";

const VERIFY_STEP = 2;
const STEPS = [
  {
    label: "Let's Start",
    success: "Customize as needed",
    error: "Failed to proceed, please contact us",
  },
  {
    label: 'Your choices',
    success: "We're setting your TimeTable, please allow us sometime",
    error: "Failed to auto assign, please contact us",
  },
  {
    label: 'Verify',
    success: "We're updating your TimeTable, please allow us sometime",
    error: "Failed to update, please contact us",
  },
  {
    label: 'Save',
    success: "Congratulation!!! You've successfully created your TimeTable",
    error: "Failed to proceed, please contact us",
  },
];

export default function CreateSchedule() {
  const stepComponentRef = useRef();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { urlParam, updateUrlParam } = useURLParams();
  const [scheduleId, setScheduleId] = useState(() => urlParam.get(URL_PARAMS.SCHEDULE_ID) || null);
  const [activeStep, setActiveStep] = useState(() => parseInt(urlParam.get(URL_PARAMS.STEP), 10) || 0);
  
  const [canBeSaved, setCanBeSaved] = useState(false);

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isApplyLoading, setIsApplyLoading] = useState(false);

  const handleUpgradeClose = () => {
    setIsUpgradeModalOpen(false);
  };

  useEffect(() => {
    if(scheduleId) {  // to avoid 1 extra re-render
      updateUrlParam(URL_PARAMS.SCHEDULE_ID, scheduleId);
    }
  }, [scheduleId]);

  // useEffect(() => {
  //   updateUrlParam(URL_PARAMS.STEP, activeStep);
  // }, [activeStep]);

  const handleNext = async () => {
    setIsNextLoading(true);
    const success = await stepComponentRef.current.onNext();
    console.log(`Step ${activeStep} : ${success}`);
    setIsNextLoading(false);
    if (success) {
      const redirect_needed = activeStep === VERIFY_STEP ? true : false;
      if ( activeStep < STEPS.length - 1) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        // addNotification(STEPS[activeStep].success, 'info');
      }
      if (redirect_needed) {
        // Redirect to Schedule after 2 seconds
        addNotification("Redirecting...", 'info');
        const timer = setTimeout(() => {
          navigate(`${AppUrls.TIME_TABLE_URL}?${URL_PARAMS.SCHEDULE_ID}=${scheduleId}`);
        }, 2000);

        // Clear timeout if the component is unmounted before the redirect
        return () => clearTimeout(timer);
      }
    } else {
      console.log('Please correct the data and retry.');
      if (STEPS[activeStep].error) {
        addNotification(STEPS[activeStep].error, 'error');
      }
    }
  };

  const handleApplyChanges = async () => {
    setIsApplyLoading(true);
    const success = await stepComponentRef.current.onApplyChanges();
    setIsApplyLoading(false);
    if (success) {
      // addNotification("Changes applied successfully", 'info'); // Done in TimeTable.js
    } else {
      addNotification("Failed to apply the changes, please try again!!!", 'error');
    }
  };

  const handleBack = () => {
    if ( activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    } else {
      addNotification("Can't take back any further", 'error');
    }
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const ValidationError = ({ errorDict }) => {
    return (
      <Box sx={{ mt: 1, maxHeight: 250, overflowY: "auto" }}>
        {/* Field Errors Section */}
        {Object.entries(errorDict).map(([field, errors]) => {
          if (!Array.isArray(errors) || errors.length === 0) return null;

          return (
            <Box key={field} sx={{ mb: 2 }}>
              <Typography color="primary">
                <strong>{`${titleCase(field)} :`}</strong>
              </Typography>

              {errors.map((error, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    ml: 2,
                    fontFamily: "monospace",
                    color: "error.main",
                  }}
                >
                  {error}
                </Typography>
              ))}
            </Box>
          );
        })}
      </Box>
    );
  };

  const StartStep = forwardRef((props, ref) => {
    const getCurrentDateTime = () => {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const yyyy = now.getFullYear();
      const mm = pad(now.getMonth() + 1);
      const dd = pad(now.getDate());
      const hh = pad(now.getHours());
      const min = pad(now.getMinutes());
      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    };
    const [scheduleLabel, setScheduleLabel] = useState(getCurrentDateTime());
    const [relatedData, setRelatedData] = useState({});
    const [selectedShift, setSelectedShift] = useState(null);
    const [sectionList, setSectionList] = useState([]);
    const [selectedSection, setSelectedSection] = useState({}); // Shift wise
    const [expanded, setExpanded] = useState(true);
    const [errorDict, setErrorDict] = useState({});

    const currentShiftSelectedSection = selectedSection[selectedShift] || [];
    
    const API_URL = 'time-table/time-table-list/';
    const SECTION_API_URL = 'academics/section-light/';

    useEffect(() => {
      const fetchItems = async () => {
        await httpRequest({
          method: 'GET',
          url: SECTION_API_URL,
          onSuccess: (data) => {
            setRelatedData(data.relatedData);
            setSelectedShift(data.relatedData?.shift[0]?.id);
            setSectionList(data.rows);

            // Selected Sections
            const grouped = {};
            data.rows.forEach((item) => {
              if (!grouped[item.shift]) grouped[item.shift] = [];
              
              grouped[item.shift].push(item.id);
            });
            setSelectedSection(grouped);
          },
          addNotification,
        });

        setIsLoading(false);
      };
      fetchItems();
    }, []);

    const relatedMap = React.useMemo(
      () => getRelatedDataMap(relatedData),
      [relatedData]
    );

    const handleToggle = (id) => {
      setSelectedSection((prev) => {
        const current = prev[selectedShift] || [];

        const alreadySelected = current.includes(id);
        let updated = [];

        if (alreadySelected) {
          if (current.length === 1) {
            addNotification("Please select at least 1 section");
            return prev;
          }
          updated = current.filter((i) => i !== id);
        } else {
          updated = [...current, id];
        }

        return {
          ...prev,
          [selectedShift]: updated,
        };
      });
    };

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    useImperativeHandle(ref, () => ({
      onNext: async () => {
        return await handleScheduleCreate();
      },
    }));

    const handleScheduleCreate = async () => {
      setErrorDict({});
      const payload = {
        name: scheduleLabel,
        shift: selectedShift,
        selected_section: selectedSection[selectedShift] || [],
      };

      const response = await httpRequest({
        method: 'POST',
        url: API_URL,
        data: payload,
        onSuccess: (data) => {
          setScheduleId(data.id);
        },
        onFailure: (data) => {
          setErrorDict(data);
        },
        addNotification,
      });
      if (response.status === 403 && response.data.code === "subscription_required") {
        addNotification(`Please upgrade to proceed`);
        navigate(MANAGE_SUBSCRIPTION_URL);
        // setIsUpgradeModalOpen(true);
      }

      return response.success;

    };

    const handleLabelChange = (event) => {
      console.log(scheduleLabel);
      setScheduleLabel(event.target.value);
    };

    return (
      <Box
        sx={{
          display: 'flex', flexDirection: 'column', gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', gap: 3,
            maxWidth: {
              xs: '100%',
              md: 400,
            },
          }}
        >
          <TextField
            fullWidth
            key="schedule_label"
            label="Schedule Name"
            name="Schedule Name"
            type="text"
            value={scheduleLabel}
            onChange={handleLabelChange}
            required={true}
            // helperText='Name for the TimeTable'
          />

          { selectedShift && (
            <TextField
              select
              fullWidth
              label="Shift"
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
              // helperText="TimeTable for Shift"
              required
              variant="outlined"
            >
              {relatedData?.shift?.map((shift_dict) => (
                <MenuItem key={shift_dict.id} value={shift_dict.id}>
                  {shift_dict.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        {/* Student Section Group */}
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            }
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5">Student Groups</Typography>
                {/* More data chips if needed */}
              </Box>
            }
            action={
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{ alignSelf: 'flex-start' }}
              >
                <ExpandCircleDownOutlinedIcon
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            }
            sx={{
              px: 2,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 1.5 } },
            }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 2, pb: 2 }}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Selected = </Typography>
                {`${currentShiftSelectedSection.length} / ${sectionList.filter(s => s.shift === selectedShift).length}`}
              </Typography>
            </Box>
          </CardContent>
  
          {/* Collapsible Content */}
          <Collapse in={expanded}>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <CardContent sx={{ bgcolor: '#FBFBFB' }}>
              <Stack direction="row" gap={1.25} flexWrap="wrap">
                {sectionList
                ?.filter((section) => section.shift === selectedShift)
                .map((item) => (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        checked={(selectedSection[selectedShift] || []).includes(item.id)}
                        onChange={() => handleToggle(item.id)}
                      />
                    }
                    label={`${item.class_level_name} ${item.section_type_name}`}
                  />
                ))}
              </Stack>
            </CardContent>
          </Collapse>
        </Card>

        {/* Validation Error */}
        {Object.keys(errorDict).length > 0 && (
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              transition: 'box-shadow 0.3s',
              '&:hover': {
                boxShadow: 3
              }
            }
          }>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5">Validation Errors</Typography>
                  {/* More data chips if needed */}
                  <WarningRoundedIcon fontSize="small" color='warning' />
                </Box>
              }
              sx={{ px: 2, py: 1.5 }}
            />
            <Divider/>
    
            {/* Collapsible Content */}
            <Collapse in={true}>
              <CardContent>
                <ValidationError errorDict={errorDict} />
              </CardContent>
            </Collapse>
          </Card>
        )}
      </Box>
    );
  });

  const ManualStep = forwardRef((props, ref) => {
    const timeTableRef = useRef();

    useImperativeHandle(ref, () => ({
      onNext: async () => {
        return await submitManualPreferences();
      },
    }));

    const submitManualPreferences = async () => {
      const success = await timeTableRef.current.onValidate('manual');
      // setIsLoading(false);
      if (success) {
        console.log("Your timetable is being created. We'll notify you once it's ready.");
        return true;
      } else {
        console.log('Please correct the manual data and retry.');
        console.log(`ManualStep: Error ${success}`);
        return false;
      }
    };

    return (
      <Box>
        <TimeTable
          scheduleId={scheduleId}
          isEditable={true}
          stage='manual'
          canBeSaved={setCanBeSaved}
          ref={timeTableRef}
        />
      </Box>
    );
  });

  const VerifyStep = forwardRef((props, ref) => {
    const timeTableRef = useRef();

    useImperativeHandle(ref, () => ({
      onNext: async () => {
        return await saveTimeTable();
      },
      onApplyChanges: async () => {
        return await submitManualPreferencesVerifyStage();
      },
    }));

    const submitManualPreferencesVerifyStage = async () => {
      const success = await timeTableRef.current.onValidate('verify');
      // setIsLoading(false);
      if (success) {
        console.log("Your timetable is being created. We'll notify you once it's ready.");
        return true;
      } else {
        console.log('Please correct the manual data and retry.');
        console.log(`VerifyStep: Error ${success}`);
        return false;
      }
    };

    const SCHEDULE_API_URL = 'time-table/time-table/';
    const saveTimeTable = async () => {
      const payload = {
        tt: scheduleId,
        stage: 'save',
      };

      const response = await httpRequest({
        method: 'PUT',
        url: SCHEDULE_API_URL,
        data: payload,
        addNotification,
      });

      return response.success;
    };

    return (
      <Box>
        <TimeTable
          scheduleId={scheduleId}
          isEditable={canBeSaved}
          stage='verify'
          canBeSaved={setCanBeSaved}
          ref={timeTableRef}
        />
      </Box>
    );
  });

  const SaveStep = forwardRef((props, ref) => {
    return (
      <Box>
        <Typography sx={{ mb: 1 }}>
          Congratulation!!! You've successfully created your TimeTable
        </Typography>
        <Typography>Please save it !!!</Typography>
      </Box>
    );
  });

  console.log(`Whats up bro`);
  const renderStepContent = useMemo(() => {
    console.log(`Render Step ${activeStep}`);
    switch (activeStep) {
      case 0:
        return <StartStep ref={stepComponentRef} />;
      case 1:
        return (
          <ManualStep ref={stepComponentRef} />
        );
      case 2:
        return (
          <VerifyStep ref={stepComponentRef} />
        );
      case 3:
        return (
          <SaveStep ref={stepComponentRef} />
        );
      default:
        return <StartStep ref={stepComponentRef} />;
    }
  }, [activeStep, canBeSaved]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        {/* Left */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Title>{TITLE}</Title>
          {activeStep === VERIFY_STEP && !canBeSaved && (
            <CircularProgress size={20} thickness={4} />
          )}
        </Box>

        {/* Right - Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {/* <Button
            variant="contained"
            color="primary"
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBeforeOutlinedIcon />}
            sx={{ px: 2 }}
          >
            Back
          </Button> */}

          { activeStep === VERIFY_STEP && (
            <Button
              variant="contained"
              color="#ffffff"
              disabled={isNextLoading || isApplyLoading || !canBeSaved}
              onClick={handleApplyChanges}
              startIcon={<CheckCircleOutlineOutlinedIcon />}
              sx={{
                px: 2,
                backgroundColor: '#0e8a0a',
                color: '#fff',
                '&:hover': { backgroundColor: '#0c7609' },
              }}
            >
              Apply
            </Button>
          )}
          
          { activeStep < STEPS.length - 1 && (
            <Button
              variant="contained"
              color="primary"
              disabled={isNextLoading || isApplyLoading || (activeStep === VERIFY_STEP && !canBeSaved)}
              onClick={handleNext}
              endIcon={<NavigateNextOutlinedIcon />}
              sx={{ px: 2 }}
            >
              {activeStep < STEPS.length - 2 ? "Next" : "Publish"}
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Stepper and Title and Button (fixed) */}
      <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
        <Stepper activeStep={activeStep}>
          {STEPS.map((step_dict, index) => {
            const stepProps = {};
            const labelProps = {};
            return (
              <Step key={step_dict.label} {...stepProps}>
                <StepLabel {...labelProps}>{step_dict.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          mt: 1,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            <Box sx={{ width: '100%', mt: 2 }}>
              {/* Main Content */}
              {renderStepContent}
            </Box>
          </Fade>
        )}
      </Box>

      {/* Upgarde Modal */}
      <Box>
        <UpgradeModal
          open={isUpgradeModalOpen}
          handleClose={handleUpgradeClose}
        />
      </Box>
    </Box>
  );
}
